/* ======================================================================
   CARD MATERIAL SWATCHES — R2 (DRAFT)
   ----------------------------------------------------------------------
   One definition, loaded once from layout/theme.liquid. It replaces the
   copy that snippets/card-material-swatches.liquid used to inline into
   every product card.

   Everything the element needs is already in the DOM as data attributes,
   precomputed in Liquid, so a click never triggers a network request.

   On selection the card is rewritten as a whole, not in part. The previous
   version updated the image, title, price and link but left the hover
   image, the badges and the wishlist payload pointing at the material the
   customer had just switched away from.
   ====================================================================== */

(() => {
  if (customElements.get('material-swatches')) return;

  /* Matches the widths product-card.liquid requests, capped at the width
     the swatch payload was generated at. */
  const SRCSET_WIDTHS = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600];

  const srcsetFor = (url) => {
    if (!url || !/width=\d+/.test(url)) return null;
    return SRCSET_WIDTHS.map((w) => url.replace(/width=\d+/, 'width=' + w) + ' ' + w + 'w').join(', ');
  };

  const applyImage = (img, url, alt) => {
    if (!img || !url) return;
    const srcset = srcsetFor(url);
    if (srcset) {
      img.setAttribute('srcset', srcset);
    } else {
      img.removeAttribute('srcset');
    }
    img.setAttribute('src', url);
    if (alt != null) img.setAttribute('alt', alt);
  };

  class MaterialSwatches extends HTMLElement {
    connectedCallback() {
      if (this.__bound) return;
      this.__bound = true;
      this.addEventListener('click', this.handleClick.bind(this));
    }

    handleClick(event) {
      const swatch = event.target.closest('.card-material-swatch');
      if (!swatch || !this.contains(swatch)) return;

      /* The anchor stays a real link so the card still works without JS and
         so cmd-click / middle-click open the material's own page. */
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;

      event.preventDefault();
      if (swatch.classList.contains('is-selected')) return;

      const card = this.closest('.product-card');
      if (!card) return;

      const d = swatch.dataset;

      this.querySelectorAll('.card-material-swatch').forEach((el) => {
        el.classList.remove('is-selected');
        el.setAttribute('aria-current', 'false');
      });
      swatch.classList.add('is-selected');
      swatch.setAttribute('aria-current', 'true');

      const label = this.querySelector('[data-material-label]');
      if (label && d.swatchLabel) label.textContent = d.swatchLabel;

      card.setAttribute('handle', d.swatchHandle);

      const mediaLink = card.querySelector('.product-card__media');
      if (mediaLink) mediaLink.setAttribute('href', d.swatchUrl);

      const figure = card.querySelector('.product-card__figure');

      applyImage(card.querySelector('.product-card__image--primary'), d.swatchImage, d.swatchImageAlt);
      this.updateSecondaryImage(card, d);
      this.updateTitle(card, d);
      this.updatePrice(card, d);
      this.updateBadges(figure, d);
      this.updateWishlist(card, d);
      this.updateQuickAdd(card, d);

      /* assets/theme.js runs Swiper on .product-card__figure--slider below
         1200px, where the two images are real slides rather than a hover
         crossfade. Slide widths are measured at init, so the instance has
         to be told the slides changed. */
      if (figure && figure.swiper) figure.swiper.update();
    }

    updateSecondaryImage(card, d) {
      let secondary = card.querySelector('.product-card__image--secondary');

      if (!d.swatchSecondaryImage) {
        if (secondary) secondary.remove();
        return;
      }

      if (!secondary) {
        const primary = card.querySelector('.product-card__image--primary');
        const media = card.querySelector('.product-card__media');
        if (!primary || !media) return;
        secondary = document.createElement('img');
        secondary.className = 'product-card__image product-card__image--secondary swiper-slide';
        secondary.setAttribute('loading', 'lazy');
        secondary.setAttribute('fetchpriority', 'low');
        secondary.setAttribute('draggable', 'false');
        const sizes = primary.getAttribute('sizes');
        if (sizes) secondary.setAttribute('sizes', sizes);
        media.appendChild(secondary);
      }

      applyImage(secondary, d.swatchSecondaryImage, d.swatchSecondaryAlt);
    }

    updateTitle(card, d) {
      const titleLink = card.querySelector('.product-title');
      if (!titleLink) return;
      titleLink.setAttribute('href', d.swatchUrl);
      if (d.swatchTitle) titleLink.textContent = d.swatchTitle;
    }

    updatePrice(card, d) {
      const priceList = card.querySelector('price-list');
      if (!priceList || !d.swatchPriceHtml) return;
      /* The payload is the theme's own price-list snippet rendered for the
         linked product, so sale, compare-at and currency formatting stay
         identical to a server-rendered card. */
      priceList.outerHTML = d.swatchPriceHtml;
    }

    updateBadges(figure, d) {
      if (!figure) return;

      let labels = [];
      try {
        labels = JSON.parse(d.swatchBadges || '[]');
      } catch (e) {
        labels = [];
      }

      const handleize = (text) =>
        text
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');

      const parts = labels.map(
        (text) => '<span class="badge badge--custom badge--' + handleize(text) + '">' + text + '</span>'
      );

      if (d.swatchSalePercent) {
        parts.push('<on-sale-badge class="badge badge--on-sale">Save ' + d.swatchSalePercent + '%</on-sale-badge>');
      }

      const existing = figure.querySelector('badge-list');

      if (!parts.length) {
        if (existing) existing.remove();
        return;
      }

      if (existing) {
        existing.innerHTML = parts.join('');
        return;
      }

      const list = document.createElement('badge-list');
      list.className = 'badge-list badge-list--vertical empty:hidden';
      list.innerHTML = parts.join('');
      figure.prepend(list);
    }

    updateWishlist(card, d) {
      const wish = card.querySelector('.product-card-button-wish');
      if (!wish) return;
      /* Swym reads these attributes at click time. Without this, the heart
         saved whichever material the card was first rendered with. */
      if (d.swatchProductId) wish.setAttribute('data-product-id', d.swatchProductId);
      if (d.swatchVariantId) wish.setAttribute('data-variant-id', d.swatchVariantId);
      if (d.swatchUrl) wish.setAttribute('data-product-url', window.location.origin + d.swatchUrl);
    }

    updateQuickAdd(card, d) {
      const input = card.querySelector('product-form form input[name="id"]');
      if (input && d.swatchVariantId) input.value = d.swatchVariantId;

      const modal = card.querySelector('quick-buy-modal');
      if (modal && d.swatchVariantId) {
        modal.setAttribute('handle', d.swatchHandle + '?variant=' + d.swatchVariantId);
      }
    }
  }

  customElements.define('material-swatches', MaterialSwatches);
})();
